'use client';

import { Loader2, Minus, Plus, ShoppingCart, Trash } from 'lucide-react';
import { toast } from 'sonner';

import type React from 'react';


import { useAuth } from '@/contexts/authContext';
import { useCartStore } from '@/core/hooks/cart/useCartStore';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { cn } from '@/core/lib/utils';
import { MainShopProduct } from '@/core/models/shop-product-model';
import Link from "next/link";
import CustomButton from '../../components/CustomButton';

interface AddToCartButtonProps {
    productInfo: MainShopProduct;
    productInventory: number;
    className?: string;
}

export const AddToCartButton: React.FC<AddToCartButtonProps> = ({
    productInfo,
    productInventory,
    className,
}) => {
    const {
        addToCart,
        updateCartItem,
        removeFromCart,
        isCartLoading,
    } = useCartStore();

    const {
        state: { isAuthenticated },
    } = useAuth();

    const { dictionary } = useDictionary();

    // گرفتن وضعیت فعلی آیتم در سبد
    const currentCart = useCartStore.getState().cart;
    const currentItem = currentCart?.items?.find(
        //@ts-ignore
        item => item.product_id === productInfo.id
    );
    const currentQuantity = currentItem?.quantity ?? 0;

    const handleAddToCart = async () => {
        if (productInventory <= 0 || isCartLoading) return;

        const availableToAdd = productInventory - currentQuantity;
        if (availableToAdd <= 0) {
            toast.warning(dictionary.ui?.product.noInventory);
            return;
        }

        try {
            await addToCart(productInfo, 1, isAuthenticated);
        } catch (error) {
            console.error('Failed to add to cart:', error);
        }
    };

    const handleIncrease = async () => {
        if (currentQuantity >= productInventory || isCartLoading) return;

        try {
            await updateCartItem(
                //@ts-ignore
                productInfo.id,
                currentQuantity + 1,
                isAuthenticated
            );
        } catch (error) {
            console.error('Failed to increase item quantity:', error);
        }
    };

    const handleDecreaseOrRemove = async () => {
        try {
            if (currentQuantity <= 1) {
                //@ts-ignore
                await removeFromCart(productInfo.id, isAuthenticated);
            } else {
                await updateCartItem(
                    //@ts-ignore
                    productInfo.id,
                    currentQuantity - 1,
                    isAuthenticated
                );
            }
        } catch (error) {
            console.error('Failed to decrease/remove item:', error);
        }
    };

    // اگه موجودی صفره
    if (productInventory <= 0) {
        return (
            <div
                className={cn(
                    'inline-flex items-center justify-center px-4 py-2 bg-red-100 text-red-700 text-sm font-medium border border-red-200 shadow-sm',
                    className
                )}
            >
                {dictionary.ui?.product.noInventory}
            </div>
        );
    }

    return (
        <div className={cn('flex flex-col gap-3', className)} dir="rtl">
            {currentQuantity > 0 ? (<>
                <div className="flex items-center justify-center gap-3 bg-gray-100 p-2">

                    {/* دکمه کم کردن / حذف */}
                    <button
                        onClick={handleDecreaseOrRemove}
                        disabled={isCartLoading}
                        className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {currentQuantity <= 1 ? <Trash size={16} /> : <Minus size={16} />}
                    </button>

                    {/* تعداد */}
                    <span className="font-medium min-w-[2rem] text-center">
                        {currentQuantity}
                    </span>

                    {/* دکمه افزایش */}
                    <button
                        onClick={handleIncrease}
                        disabled={isCartLoading || currentQuantity >= productInventory}
                        className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus size={16} />
                    </button>


                </div>
                <div className="flex items-center justify-center gap-3 bg-gray-100 p-2">
                    <Link
                        href="/cart"
                        className=" flex w-full items-center justify-center"
                    >
                        {dictionary.ui?.product.goToCart}
                        <ShoppingCart size={18} className="stroke-1 pr-1" />
                    </Link>
                </div>
            </>
            ) : (
                <CustomButton
                    onClick={handleAddToCart}
                    disabled={isCartLoading}
                    size="full"
                    className="flex items-center justify-center gap-2"
                >
                    <Plus size={18} />
                    {isCartLoading ? <Loader2 className='animate-spin' /> : dictionary.common.add}
                </CustomButton>
            )}
        </div>
    );
};
